import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  inject,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  WarehouseLocalizationDetailsControlsPossibleToHide,
  WarehouseLocalizationDetailsForm,
  WarehouseLocalizationDetailsFormGroup,
} from '../../models/warehouse-localization-details-form';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { Nillable } from '@web/utils';

@Component({
  selector: 'app-warehouse-localization-details-form',
  imports: [
    ReactiveFormsModule,
    FormControlComponent,
    PrimengInputTextDirective,
    InputTextModule,
    Button,
    PrimengButtonDirective,
  ],
  templateUrl: './warehouse-localization-details-form.component.html',
  styleUrl: './warehouse-localization-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseLocalizationDetailsFormComponent {
  private readonly builder = inject(FormBuilder);

  public readonly data = input<Nillable<WarehouseLocalizationDetailsForm>>();
  /* prettier-ignore */
  public readonly hiddenControls = input<WarehouseLocalizationDetailsControlsPossibleToHide[]>([]);
  public readonly submitClickEvent = output<WarehouseLocalizationDetailsForm>();

  protected readonly form = this.buildForm();

  public constructor() {
    effect(() => this.fillByData());
    effect(() => this.hiddenControlsRequirement());
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitClickEvent.emit(
      new WarehouseLocalizationDetailsForm(data.label, data.position),
    );
  }

  public shouldBeVisible(
    control: WarehouseLocalizationDetailsControlsPossibleToHide,
  ): boolean {
    return !this.hiddenControls().includes(control);
  }

  private buildForm(): WarehouseLocalizationDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      label: this.builder.nonNullable.control<string>('', [Validators.required]),
      position: this.builder.nonNullable.control<number>(0, [Validators.required]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      label: data?.label,
      position: data?.position,
    });
  }

  private hiddenControlsRequirement(): void {
    this.shouldBeVisible('position')
      ? this.enablePositionValidation()
      : this.disablePositionValidation();
  }

  private enablePositionValidation(): void {
    this.form.controls.position.setValidators([Validators.required]);
    this.form.controls.position.updateValueAndValidity();
    this.form.controls.position.enable();
  }

  private disablePositionValidation(): void {
    this.form.controls.position.clearValidators();
    this.form.controls.position.updateValueAndValidity();
    this.form.controls.position.disable();
  }
}
